import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, AlertTriangle, AlertOctagon, CheckCircle2, ArrowRight } from "lucide-react";
import { authAxios } from "../../utils/auth";

/**
 * GuardianCard — Dashboard summary of Guardian fraud posture.
 *
 * Renders the aggregate threat CODE (GREEN / AMBER / RED / HIGH_ALERT),
 * a plain-English headline, and up to four contributing reasons pulled
 * from /admin/fraud/threat-summary. Clicking the card deep-links to the
 * full Guardian tab.
 *
 * Props:
 *   compact — bool, use a tighter footprint (dashboard grid variant).
 */
export default function GuardianCard({ compact = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    authAxios.get("/admin/fraud/threat-summary")
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const style = STYLES[data?.code || "GREEN"];
  const Icon = style.icon;
  const flashing = data?.code === "HIGH_ALERT";
  const onOpen = () => navigate("/admin/analytics?tab=guardian");
  const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } };

  // Scroll to top when navigating so the newly-mounted Guardian tab is visible.
  const goGuardian = (e) => {
    e?.stopPropagation();
    onOpen();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div
        className={`rounded-xl border border-white/[0.06] bg-[#111113] ${compact ? "p-4 h-28" : "p-5 h-40"} animate-pulse`}
        data-testid="guardian-card-loading"
      />
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goGuardian}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goGuardian(e); } }}
      data-testid="guardian-card"
      className={`
        relative overflow-hidden rounded-xl border p-5 cursor-pointer transition-all
        ${style.card}
        ${flashing ? "animate-guardian-flash" : "hover:border-white/20"}
      `}
    >
      {/* Flashing keyframe — inline so we don't touch tailwind config */}
      {flashing && (
        <style>{`
          @keyframes guardian-flash {
            0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.55); border-color: rgba(239,68,68,0.9); }
            50%      { box-shadow: 0 0 22px 4px rgba(239,68,68,0.75); border-color: rgba(255,80,80,1); }
          }
          .animate-guardian-flash { animation: guardian-flash 1.2s ease-in-out infinite; }
        `}</style>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${style.iconTone}`} />
          <span className="text-white/40 text-xs font-medium uppercase tracking-wider">
            Guardian
          </span>
        </div>
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${style.badge}`}
          data-testid="guardian-code-badge"
        >
          {data?.code === "HIGH_ALERT" ? "High Alert" : data?.code || "GREEN"}
        </span>
      </div>

      <p
        className={`mt-3 text-sm font-semibold ${style.headline}`}
        data-testid="guardian-headline"
      >
        {data?.headline || "All clear — no active fraud pressure detected."}
      </p>

      {(data?.reasons || []).length > 0 && (
        <ul className="mt-2 space-y-0.5" data-testid="guardian-reasons">
          {data.reasons.slice(0, compact ? 2 : 4).map((r, i) => (
            <li key={i} className="text-[11px] text-white/60 flex gap-2">
              <span className={`mt-1 w-1 h-1 rounded-full flex-shrink-0 ${style.dot}`} />
              <span className="min-w-0 truncate">{r}</span>
            </li>
          ))}
          {data.reasons.length > (compact ? 2 : 4) && (
            <li className="text-[10px] text-white/30 pl-3">
              +{data.reasons.length - (compact ? 2 : 4)} more…
            </li>
          )}
        </ul>
      )}

      <div className="mt-3 flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70">
        Open Guardian <ArrowRight className="w-3 h-3" />
      </div>
    </div>
  );
}

const STYLES = {
  GREEN: {
    icon: CheckCircle2,
    iconTone: "text-emerald-400",
    card: "bg-emerald-500/[0.04] border-emerald-500/20",
    badge: "bg-emerald-500/15 text-emerald-300",
    headline: "text-emerald-200",
    dot: "bg-emerald-400",
  },
  AMBER: {
    icon: AlertTriangle,
    iconTone: "text-amber-300",
    card: "bg-amber-500/[0.05] border-amber-500/25",
    badge: "bg-amber-500/15 text-amber-300",
    headline: "text-amber-100",
    dot: "bg-amber-400",
  },
  RED: {
    icon: AlertOctagon,
    iconTone: "text-red-400",
    card: "bg-red-500/[0.06] border-red-500/30",
    badge: "bg-red-500/20 text-red-300",
    headline: "text-red-100",
    dot: "bg-red-400",
  },
  HIGH_ALERT: {
    icon: AlertOctagon,
    iconTone: "text-red-300",
    card: "bg-red-500/[0.10] border-red-500/60 shadow-[0_0_18px_-4px_rgba(239,68,68,0.55)]",
    badge: "bg-red-600/40 text-red-100 uppercase",
    headline: "text-red-50",
    dot: "bg-red-300",
  },
};
